import { useSelector } from "react-redux";
import { useFormContext } from "react-hook-form";
import CustomerFormAction from "@common/redux/actions/CustomerFormAction";
import {
  castCustomerForSubmission,
  mapCustomersToOptions,
} from "../helpers/Customer/customers";
import { appendPlaceholders, transformData } from "../helpers/identity";
import {
  fetchCustomerRowVersion,
  searchCustomerById,
} from "../helpers/Customer/searchCustomer";
import CustomerClient from "@common/api/CustomerClient";
import { handleError } from "../helpers/error";
import LoadingAction from "@common/redux/actions/LoadingAction";
import AlertModalAction from "@common/redux/actions/AlertModalAction";
import useLogin from "./useLogin";
import usePhoto from "./usePhoto";
import useCustomerEdit from "./useCustomerEdit";

const useCustomer = () => {
  const [{ isDesktop }] = useLogin();
  const { formType, customers } = useSelector((store) =>
    CustomerFormAction.getStatePath(store),
  );
  const setCustomerFormState = (data) => CustomerFormAction.dispatch(data);
  const { watch, setValue, resetField } = useFormContext();

  const { setPhotoState, getPhoto } = usePhoto();

  const { updateCustomer } = useCustomerEdit({
    handleEditCustomer: () => {},
    customers: [],
  });

  const hasMultipleCustomers =
    formType === "suspiciousActivity" || formType === "customerIncident";

  const setPhoneAndEmail = (customer) => {
    setValue("customer.phoneNumber", customer.phoneNumber, {
      shouldValidate: true,
    });
    setValue("customer.emailAddress", customer.emailAddress, {
      shouldValidate: true,
    });
  };

  const createCustomer = (customerData) => {
    LoadingAction.set(true).then(() => {
      CustomerClient.Create(customerData).then(
        ({ id }) => {
          LoadingAction.set(false);
          AlertModalAction.open("Successfully created customer!", "Success");
          if (!hasMultipleCustomers) {
            setValue("customerId", id, { shouldValidate: true });
          } else {
            const customerIds = watch("customerIds");
            if (customerIds && !customerIds.includes(id)) {
              setValue("customerIds", [...customerIds, id], {
                shouldValidate: true,
              });
            }
          }
        },
        (error) => handleError(error),
      );
    });
  };

  const onSelectCustomer = async (customerId) => {
    const rowVersion = await fetchCustomerRowVersion(customerId);
    const customerWithPhotos = await searchCustomerById({
      id: customerId,
      includeImages: true,
    });
    const parsedCustomers = mapCustomersToOptions(customerWithPhotos.data);
    const customer = {
      ...parsedCustomers.find((x) => x.key === customerId),
      rowVersion,
    };
    if (!customer) {
      console.warn("Filtered customer is not found!");
      return;
    }

    if (customer && customer.images) {
      setPhotoState({ photo: getPhoto(customer) });
    }

    // Single Customer
    if (!hasMultipleCustomers) {
      setValue("customer", castCustomerForSubmission(customer), {
        shouldValidate: true,
      });
      setValue("customerId", customerId, { shouldValidate: true });
      setValue("hideCustomerSearch", true);
      setCustomerFormState({ customers: [customer] });
      if (formType === "unresolvedDispute") setPhoneAndEmail(customer);
      return;
    }

    resetField("searchedCustomer");
    resetField("customerUnknown");

    // Multiple Customers
    setCustomerFormState({ customers: [...(customers || []), customer] });
    setValue("customers", [...(customers || []), customer], {
      shouldValidate: true,
    });
    const customerIds = watch("customerIds");
    if (!customerIds || customerIds?.includes(customerId)) {
      return;
    }
    setValue("customerIds", [...customerIds, customerId], {
      shouldValidate: true,
    });
  };

  const onNewCustomer = (customer) => {
    const customerData = appendPlaceholders(customer);
    const tableData = transformData(customer);
    if (watch("customerUnknown")) customer.name = "Unknown";
    customer.rowVersion = "";

    // Create a new customer separately before Update event in CMS Desktop
    if (isDesktop) createCustomer(customerData);

    // Single Customer
    if (!hasMultipleCustomers) {
      if (!isDesktop) {
        setValue("customer", customerData, { shouldValidate: true });
        setValue("customerId", null, { shouldValidate: true });
        formType === "unresolvedDispute" && setPhoneAndEmail(customer);
      }
      setValue("hideCustomerSearch", true);
      setCustomerFormState({ customers: [{ ...customer, ...tableData }] });
      return;
    }

    resetField("searchedCustomer");
    resetField("customerUnknown");

    // Multiple Customers
    setCustomerFormState({
      customers: [...(customers || []), { ...customer, ...tableData }],
    });
    if (!isDesktop) {
      setValue("customers", [...(customers || []), customer], {
        shouldValidate: true,
      });
    }
  };

  const onEditCustomer = (customer) => {
    const currentCustomer = customers.find((x) => x.id === customer.id);
    const customerData = appendPlaceholders(customer);
    customerData.hasBeenModified = true;
    const tableData = transformData(customer);

    if (customerData.id) {
      // Only edit a customer if it already exists with an id
      updateCustomer(customerData);
    } else if (isDesktop) {
      // Create a new customer separately before Update event in CMS Desktop
      createCustomer(customerData);
    }

    // Single Customer
    if (!hasMultipleCustomers) {
      setValue("customer", customerData, { shouldValidate: true });
      setCustomerFormState({
        customers: [{ ...currentCustomer, ...customer, ...tableData }],
      });
      if (formType === "unresolvedDispute") setPhoneAndEmail(customer);
      return;
    }

    // Multiple Customers
    const existingCustomers = filterCustomers(customers, customer);
    setCustomerFormState({
      customers: [
        ...(existingCustomers || []),
        { ...currentCustomer, ...customer, ...tableData },
      ],
    });
    setValue("customers", [...(existingCustomers || []), customer], {
      shouldValidate: true,
    });

    if (formType === "suspiciousActivity") {
      if (isDesktop) return;
      const customers = watch("customers");
      const existingCustomers = filterCustomers(customers, customer);
      setValue("customers", [...(existingCustomers || []), customer], {
        shouldValidate: true,
      });
    }
  };

  const removeCustomer = (customers, customer) =>
    customers.filter((x) => x.id !== customer.id);

  const removeCustomerIds = (customerIds, customer) =>
    customerIds.filter((x) => x !== customer.id);

  const filterCustomers = (customers, customer) =>
    customers.filter((x) => x.id !== customer.id);

  const onRemoveCustomer = (customer = null) => {
    // Single Customer
    setValue("customer", null, { shouldValidate: true });
    setValue("customerId", null, { shouldValidate: true });
    setValue("hideCustomerSearch", null);

    if (!hasMultipleCustomers && !customer) {
      setCustomerFormState({ customers: null });
      return;
    }

    // Multiple Customers
    const [customers, customerIds] = watch(["customers", "customerIds"]);
    setCustomerFormState({ customers: removeCustomer(customers, customer) });
    if (customers) {
      setValue("customers", removeCustomer(customers, customer), {
        shouldValidate: true,
      });
    }
    if (customers)
      setValue("customers", removeCustomer(customers, customer), {
        shouldValidate: true,
      });
    if (customerIds)
      setValue("customerIds", removeCustomerIds(customerIds, customer), {
        shouldValidate: true,
      });
  };

  return { onSelectCustomer, onNewCustomer, onEditCustomer, onRemoveCustomer };
};

export default useCustomer;
